/*
 * @Author: Ryan
 * @Date: 2026-01-09
 * @Description: VS Comparison Section for Video Translator landing page
 */
import { useRouter } from 'next/router';
import React, { useContext } from 'react';

import StartButton from '@/3.0/ai-avatar-generator/seo/StartButton';
import { RightContext } from '@/components/RightProvider/context';
import { VIDEO_TRANSLATOR_URL } from '@/landing/video-translator/config';

import styles from './index.module.scss';

export interface VsSectionProps {
  /** 区块标题 */
  title: string;
  /** 区块副标题 */
  subtitle: string;
  /** 旧方式标题 */
  oldTitle: string;
  /** 旧方式要点列表 */
  oldPoints: string[];
  /** 旧方式总结 */
  oldConclusion: string;
  /** 新方式标题 */
  newTitle: string;
  /** 新方式要点列表 */
  newPoints: string[];
  /** 新方式总结 */
  newConclusion: string;
  /** CTA 按钮文本 */
  ctaText: string;
}

// X 图标
const XIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 4L4 12M4 4L12 12"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 勾选图标
const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.5 4.5L6 12L2.5 8.5"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VsSection: React.FC<VsSectionProps> = ({
  title,
  subtitle,
  oldTitle,
  oldPoints,
  oldConclusion,
  newTitle,
  newPoints,
  newConclusion,
  ctaText,
}) => {
  const router = useRouter();
  const rightContext = useContext(RightContext);

  // CTA 点击
  const handleCtaClick = async () => {
    if (!rightContext.getIsLoggedIn()) {
      await rightContext.getUserLoginState({
        onReady: () => {
          router.push(VIDEO_TRANSLATOR_URL);
        },
      });
      return;
    }
    router.push(VIDEO_TRANSLATOR_URL);
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题 */}
        <h2 className={styles.title}>{title}</h2>

        {/* 副标题 */}
        <p className={styles.subtitle}>{subtitle}</p>

        {/* 对比卡片 */}
        <div className={styles.cardsWrapper}>
          {/* Old Way 卡片 */}
          <div className={styles.oldCard}>
            {/* Header */}
            <div className={styles.cardHeader}>
              <div className={styles.oldIconWrapper}>
                <XIcon />
              </div>
              <h3 className={styles.cardTitle}>{oldTitle}</h3>
            </div>

            {/* 分隔线 */}
            <div className={styles.divider} />

            {/* 要点列表 */}
            <ul className={styles.pointList}>
              {oldPoints.map((point, index) => (
                <li key={index} className={styles.oldPoint}>
                  <span className={styles.oldBullet}>•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            {/* 总结 */}
            <div className={styles.oldConclusion}>
              <span>😫</span>
              <span>{oldConclusion}</span>
            </div>
          </div>

          {/* New Way 卡片 */}
          <div className={styles.newCard}>
            {/* Header */}
            <div className={styles.cardHeader}>
              <div className={styles.newIconWrapper}>
                <CheckIcon />
              </div>
              <h3 className={styles.cardTitle}>{newTitle}</h3>
            </div>

            {/* 分隔线 */}
            <div className={styles.divider} />

            {/* 要点列表 */}
            <ul className={styles.pointList}>
              {newPoints.map((point, index) => (
                <li key={index} className={styles.newPoint}>
                  <span className={styles.newBullet}>•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            {/* 总结 */}
            <div className={styles.newConclusion}>
              <span>🚀</span>
              <span>{newConclusion}</span>
            </div>
          </div>
        </div>

        {/* CTA 按钮 */}
        <div className={styles.ctaWrapper}>
          <StartButton
            text={ctaText}
            onClick={handleCtaClick}
            className={styles.ctaButton}
          />
        </div>
      </div>
    </section>
  );
};

export default VsSection;
